
import React, { useEffect, useState } from 'react';
import { PlusCircle, Wallet, Shield, Smartphone, BarChart3, Star, ChevronDown, ChevronUp, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import LandingChatbot from './LandingChatbot';

const Index = () => {
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [openFaq, setOpenFaq] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const navigate = useNavigate();

    const testimonials = [
        {
            name: "Alex Johnson",
            role: "Small Business Owner",
            image: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop&crop=face",
            content: "This expense tracker completely transformed how I manage my business finances. The intuitive interface and powerful insights have saved me hours each week.",
            rating: 5
        },
        {
            name: "Michael Chen",
            role: "Freelance Designer",
            image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
            content: "I've tried many financial apps, but this one stands out. The real-time tracking and budget management features are exactly what I needed.",
            rating: 5
        },
        {
            name: "Emily Rodriguez",
            role: "Marketing Manager",
            image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
            content: "The automated categorization and detailed analytics helped me identify spending patterns I never noticed before. Highly recommended!",
            rating: 5
        }
    ];

    const faqs = [
        {
            question: "How secure is my financial data?",
            answer: "We use bank-level encryption and security measures to protect your data. All information is encrypted both in transit and at rest, and we never store your bank login credentials."
        },
        {
            question: "Can I use this app on multiple devices?",
            answer: "Yes! Your data syncs seamlessly across all your devices. Whether you're on your phone, tablet, or computer, you'll always have access to your latest financial information."
        },
        {
            question: "Is there a free trial available?",
            answer: "Absolutely! We offer a 30-day free trial with full access to all premium features. No credit card required to get started."
        },
        {
            question: "Can I export my financial data?",
            answer: "Yes, you can export your transaction history and reports in various formats including CSV, PDF, and Excel for your records or tax preparation."
        },
        {
            question: "How does the automatic categorization work?",
            answer: "Our smart AI analyzes your transactions and automatically categorizes them based on merchant names, transaction amounts, and spending patterns. You can always review and adjust categories as needed."
        },
        {
            question: "What payment methods do you accept?",
            answer: "We accept all major credit cards, PayPal, and bank transfers. All payments are processed securely through industry-standard payment processors."
        }
    ];

    const features = [
        {
            icon: <Wallet className="w-8 h-8" />,
            title: "Smart Budget Management",
            description: "Set budgets, track spending, and get intelligent alerts when you're approaching your limits."
        },
        {
            icon: <BarChart3 className="w-8 h-8" />,
            title: "Detailed Analytics",
            description: "Visualize your spending patterns with beautiful charts and comprehensive financial reports."
        },
        {
            icon: <Shield className="w-8 h-8" />,
            title: "Bank-Level Security",
            description: "Your financial data is protected with end-to-end encryption and advanced security protocols."
        },
        {
            icon: <Smartphone className="w-8 h-8" />,
            title: "Multi-Device Sync",
            description: "Access your financial data anywhere, anytime. Seamlessly sync across all your devices."
        }
    ];

    const handleSignIn = () => {
        navigate('/login');
    };

    const handleRegister = () => {
        navigate('/register');

    };

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 4000); // Change testimonial every 4 seconds

        return () => clearInterval(interval);
    }, [testimonials.length]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [windowWidth]);

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
            <LandingChatbot />
            {/* Header */}
            <header style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                position: 'sticky',
                top: 0,
                zIndex: 1000
            }}>
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '20px 20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    {/* Logo */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                        }}>
                            <Wallet size={20} color="white" />
                        </div>
                        <h1 style={{
                            color: 'white',
                            fontSize: windowWidth <= 768 ? '20px' : '24px',
                            fontWeight: '700',
                            margin: 0
                        }}>
                            ExpenseTracker
                        </h1>
                    </div>

                    {/* Desktop Navigation */}
                    <div style={{
                        display: windowWidth <= 768 ? 'none' : 'flex',
                        gap: '15px',
                        alignItems: 'center'
                    }}>
                        <button
                            onClick={handleSignIn}
                            style={{
                                background: 'rgba(255, 255, 255, 0.15)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: '12px',
                                color: 'white',
                                padding: '12px 24px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '14px',
                                transition: 'all 0.3s ease',
                                backdropFilter: 'blur(10px)'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                                e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                                e.target.style.transform = 'translateY(0)';
                            }}
                        >
                            Sign In
                        </button>
                        <button
                            onClick={handleRegister}
                            style={{
                                background: 'white',
                                border: 'none',
                                borderRadius: '12px',
                                color: '#667eea',
                                padding: '12px 24px',
                                cursor: 'pointer',
                                fontWeight: '700',
                                fontSize: '14px',
                                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 6px 25px rgba(0, 0, 0, 0.2)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                            }}
                        >
                            Get Started
                        </button>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        style={{
                            display: windowWidth <= 768 ? 'flex' : 'none',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '10px',
                            color: 'white',
                            padding: '10px',
                            cursor: 'pointer',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        }}
                    >
                        {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div style={{
                        display: windowWidth <= 768 ? 'block' : 'none',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                        padding: '20px',
                        animation: 'slideDown 0.3s ease-out'
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '15px',
                            maxWidth: '300px',
                            margin: '0 auto'
                        }}>
                            <button
                                onClick={() => {
                                    handleSignIn();
                                    setIsMobileMenuOpen(false);
                                }}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    borderRadius: '12px',
                                    color: 'white',
                                    padding: '15px 24px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '16px',
                                    width: '100%',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                                }}
                            >
                                Sign In
                            </button>
                            <button
                                onClick={() => {
                                    handleRegister();
                                    setIsMobileMenuOpen(false);
                                }}
                                style={{
                                    background: 'white',
                                    border: 'none',
                                    borderRadius: '12px',
                                    color: '#667eea',
                                    padding: '15px 24px',
                                    cursor: 'pointer',
                                    fontWeight: '700',
                                    fontSize: '16px',
                                    width: '100%',
                                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                                }}
                            >
                                Get Started
                            </button>
                        </div>
                    </div>
                )}
            </header>

            <div className="banner-container" style={{
                maxWidth: '1200px',
                width: '100%',
                margin: '24px auto',
                display: 'grid',
                gridTemplateColumns: windowWidth <= 768 ? '1fr' : '1fr 1fr',
                gap: '40px',
                alignItems: 'center',
            }}>
                {/* Left Content */}
                <div className='left-content' style={{
                    color: 'white',
                    order: windowWidth <= 768 ? 2 : 1
                }}>
                    {/* <div style={{
                        marginBottom: '20px'
                    }}>
                        <span style={{
                            background: 'rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '25px',
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: 'rgba(255, 255, 255, 0.9)',
                            display: 'inline-block'
                        }}>
                            ✨ New Features Available
                        </span>
                    </div> */}

                    <h1 style={{
                        fontSize: windowWidth <= 768 ? '32px' : '48px',
                        fontWeight: '800',
                        margin: '0 0 20px 0',
                        lineHeight: '1.2'
                    }}>
                        Track Your Expenses Effortlessly
                    </h1>

                    <p style={{
                        fontSize: windowWidth <= 768 ? '16px' : '18px',
                        color: 'rgba(255, 255, 255, 0.8)',
                        lineHeight: '1.6',
                        margin: '0 0 30px 0',
                        maxWidth: '500px'
                    }}>
                        Manage your spending, monitor income, and stay in control of your finances from anywhere, on any device..
                    </p>

                    {/* Feature Points */}
                    <div style={{
                        marginBottom: '40px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '12px'
                        }}>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                background: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <div style={{
                                    width: '6px',
                                    height: '6px',
                                    background: 'white',
                                    borderRadius: '50%'
                                }} />
                            </div>
                            <span style={{
                                fontSize: '16px',
                                color: 'rgba(255, 255, 255, 0.9)'
                            }}>
                                Real-time income & expense tracking
                            </span>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '12px'
                        }}>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                background: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <div style={{
                                    width: '6px',
                                    height: '6px',
                                    background: 'white',
                                    borderRadius: '50%'
                                }} />
                            </div>
                            <span style={{
                                fontSize: '16px',
                                color: 'rgba(255, 255, 255, 0.9)'
                            }}>
                                Visual insights into spending habits
                            </span>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <div style={{
                                width: '20px',
                                height: '20px',
                                background: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <div style={{
                                    width: '6px',
                                    height: '6px',
                                    background: 'white',
                                    borderRadius: '50%'
                                }} />
                            </div>
                            <span style={{
                                fontSize: '16px',
                                color: 'rgba(255, 255, 255, 0.9)'
                            }}>
                                Accessible anytime on all devices
                            </span>
                        </div>
                    </div>

                    {/* CTA Buttons */}
                    <div style={{
                        display: 'flex',
                        gap: '15px',
                        flexWrap: 'wrap'
                    }}>
                        <button style={{
                            background: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            color: '#667eea',
                            padding: windowWidth <= 768 ? '14px 24px' : '16px 32px',
                            fontSize: '16px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                            transition: 'all 0.3s ease'
                        }}
                            onMouseOver={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.3)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.2)';
                            }}>
                            <PlusCircle size={18} />
                            Start Tracking
                        </button>

                        <button style={{
                            background: 'rgba(255, 255, 255, 0.15)',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '12px',
                            color: 'white',
                            padding: windowWidth <= 768 ? '12px 24px' : '14px 32px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.3s ease'
                        }}
                            onMouseOver={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                                e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                                e.target.style.transform = 'translateY(0)';
                            }}>
                            View Demo
                        </button>
                    </div>
                </div>

                {/* Right Image/Visual */}
                <div style={{
                    order: windowWidth <= 768 ? 1 : 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <div style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: '500px',
                        height: windowWidth <= 768 ? '300px' : '400px'
                    }}>
                        {/* Main Device Mockup */}
                        <div style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '25px',
                            border: '2px solid rgba(255, 255, 255, 0.2)',
                            padding: '30px',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {/* Animated Background Elements */}
                            <div style={{
                                position: 'absolute',
                                top: '20px',
                                right: '20px',
                                width: '60px',
                                height: '60px',
                                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                borderRadius: '15px',
                                opacity: '0.8',
                                animation: 'float 3s ease-in-out infinite'
                            }} />

                            <div style={{
                                position: 'absolute',
                                bottom: '30px',
                                left: '30px',
                                width: '40px',
                                height: '40px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '10px',
                                opacity: '0.6',
                                animation: 'float 3s ease-in-out infinite 1s'
                            }} />

                            {/* Central Icon */}
                            <div style={{
                                width: '120px',
                                height: '120px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 15px 35px rgba(0, 0, 0, 0.3)',
                                marginBottom: '20px',
                                animation: 'pulse 2s ease-in-out infinite'
                            }}>
                                <Wallet size={48} color="white" />
                            </div>

                            {/* Feature Cards */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '15px',
                                width: '100%',
                                maxWidth: '300px'
                            }}>
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '12px',
                                    padding: '15px',
                                    textAlign: 'center',
                                    border: '1px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                    <div style={{
                                        fontSize: '24px',
                                        fontWeight: '700',
                                        color: 'white',
                                        marginBottom: '5px'
                                    }}>
                                        24/7
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: 'rgba(255, 255, 255, 0.8)'
                                    }}>
                                        Access Anywhere
                                    </div>
                                </div>

                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '12px',
                                    padding: '15px',
                                    textAlign: 'center',
                                    border: '1px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                    <div style={{
                                        fontSize: '24px',
                                        fontWeight: '700',
                                        color: 'white',
                                        marginBottom: '5px'
                                    }}>
                                        1-Click
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: 'rgba(255, 255, 255, 0.8)'
                                    }}>
                                        Add Expenses
                                    </div>
                                </div>

                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '12px',
                                    padding: '15px',
                                    textAlign: 'center',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    gridColumn: '1 / -1'
                                }}>
                                    <div style={{
                                        fontSize: '20px',
                                        fontWeight: '700',
                                        color: 'white',
                                        marginBottom: '5px'
                                    }}>
                                        Smart Tracking
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: 'rgba(255, 255, 255, 0.8)'
                                    }}>
                                        Personalized & Secure
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Floating Elements */}
                        <div style={{
                            position: 'absolute',
                            top: '-10px',
                            left: '-10px',
                            width: '30px',
                            height: '30px',
                            background: 'rgba(255, 255, 255, 0.3)',
                            borderRadius: '50%',
                            animation: 'bounce 2s ease-in-out infinite'
                        }} />

                        <div style={{
                            position: 'absolute',
                            bottom: '-15px',
                            right: '50px',
                            width: '20px',
                            height: '20px',
                            background: 'rgba(255, 215, 0, 0.7)',
                            borderRadius: '50%',
                            animation: 'bounce 2s ease-in-out infinite 0.5s'
                        }} />
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes float {
                    0%, 100% {
                        transform: translateY(0px);
                    }
                    50% {
                        transform: translateY(-10px);
                    }
                }

                @keyframes pulse {
                    0%, 100% {
                        transform: scale(1);
                    }
                    50% {
                        transform: scale(1.05);
                    }
                }

                @keyframes bounce {
                    0%, 100% {
                        transform: translateY(0px);
                    }
                    50% {
                        transform: translateY(-5px);
                    }
                }

                @media (max-width: 1200px) {
                    .banner-container {
                        max-width: unset!important;
                        width: fit-content!important;
                        padding: 0 20px;
                    }
                }

                @media (max-width: 768px) {
                    .banner-grid {
                        grid-template-columns: 1fr !important;
                        gap: 30px !important;
                        text-align: center;
                    }
                    
                    .left-content {
                        margin-top: 64px;
                    }
                }

                @media (max-width: 480px) {
                    .banner-buttons {
                        flex-direction: column !important;
                        width: 100%;
                    }
                    
                    .banner-buttons button {
                        width: 100% !important;
                        justify-content: center !important;
                    }
                }
            `}</style>

            <style>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @media (max-width: 768px) {
                    /* Additional mobile-specific styles if needed */
                }
            `}</style>

            {/* Hero Section */}
            <section style={{
                padding: '80px 20px',
                textAlign: 'center',
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
                <div style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    padding: '60px 40px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
                }}>
                    <h1 style={{
                        color: 'white',
                        fontSize: '48px',
                        fontWeight: '800',
                        margin: '0 0 20px 0',
                        lineHeight: '1.2'
                    }} className="hero-title">
                        Take Control of Your
                        <br />
                        <span style={{
                            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}>
                            Financial Future
                        </span>
                    </h1>

                    <p style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '20px',
                        margin: '0 0 40px 0',
                        maxWidth: '600px',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        lineHeight: '1.6'
                    }} className="hero-subtitle">
                        Track expenses, manage budgets, and gain insights into your spending habits with our intuitive and powerful expense tracking platform.
                    </p>

                    <div style={{
                        display: 'flex',
                        gap: '20px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <button
                            onClick={handleRegister}
                            style={{
                                background: 'white',
                                border: 'none',
                                borderRadius: '15px',
                                color: '#667eea',
                                padding: '18px 36px',
                                fontSize: '16px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.transform = 'translateY(-3px)';
                                e.target.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.3)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.2)';
                            }}
                        >
                            <PlusCircle size={20} />
                            Start Free Trial
                        </button>

                        <button
                            onClick={handleSignIn}
                            style={{
                                background: 'rgba(255, 255, 255, 0.15)',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: '15px',
                                color: 'white',
                                padding: '16px 36px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                                e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                                e.target.style.transform = 'translateY(0)';
                            }}
                        >
                            Watch Demo
                        </button>
                    </div>

                    <div style={{
                        marginTop: '50px',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '40px',
                        flexWrap: 'wrap'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{
                                fontSize: '32px',
                                fontWeight: '800',
                                color: 'white',
                                margin: '0 0 5px 0'
                            }}>10k+</div>
                            <div style={{
                                fontSize: '14px',
                                color: 'rgba(255, 255, 255, 0.8)'
                            }}>Active Users</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{
                                fontSize: '32px',
                                fontWeight: '800',
                                color: 'white',
                                margin: '0 0 5px 0'
                            }}>$2M+</div>
                            <div style={{
                                fontSize: '14px',
                                color: 'rgba(255, 255, 255, 0.8)'
                            }}>Money Tracked</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{
                                fontSize: '32px',
                                fontWeight: '800',
                                color: 'white',
                                margin: '0 0 5px 0'
                            }}>4.9★</div>
                            <div style={{
                                fontSize: '14px',
                                color: 'rgba(255, 255, 255, 0.8)'
                            }}>User Rating</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section style={{
                padding: '80px 20px',
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
                <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                    <h2 style={{
                        color: 'white',
                        fontSize: '36px',
                        fontWeight: '700',
                        margin: '0 0 20px 0'
                    }}>
                        Powerful Features for Modern Finance
                    </h2>
                    <p style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '18px',
                        maxWidth: '600px',
                        margin: '0 auto'
                    }}>
                        Everything you need to manage your finances effectively, all in one beautiful and intuitive platform.
                    </p>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '30px'
                }}>
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            style={{
                                background: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '20px',
                                padding: '40px 30px',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                textAlign: 'center',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-10px)';
                                e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.2)';
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                            }}
                        >
                            <div style={{
                                width: '80px',
                                height: '80px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 25px auto',
                                color: 'white',
                                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
                            }}>
                                {feature.icon}
                            </div>
                            <h3 style={{
                                color: 'white',
                                fontSize: '22px',
                                fontWeight: '600',
                                margin: '0 0 15px 0'
                            }}>
                                {feature.title}
                            </h3>
                            <p style={{
                                color: 'rgba(255, 255, 255, 0.8)',
                                fontSize: '16px',
                                lineHeight: '1.6',
                                margin: 0
                            }}>
                                {feature.description}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Testimonials Section */}
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px'
            }}>
                <section style={{
                    maxWidth: '1200px',
                    width: '100%'
                }}>
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <h2 style={{
                            color: 'white',
                            fontSize: '36px',
                            fontWeight: '700',
                            margin: '0 0 20px 0'
                        }}>
                            What Our Users Say
                        </h2>
                        <p style={{
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '18px',
                            maxWidth: '600px',
                            margin: '0 auto'
                        }}>
                            Join thousands of satisfied users who have transformed their financial management.
                        </p>
                    </div>

                    <div style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '20px',
                        padding: '50px 40px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        textAlign: 'center',
                        maxWidth: '800px',
                        margin: '0 auto',
                        position: 'relative',
                        overflow: 'hidden',
                        minHeight: '350px'
                    }}>
                        {/* Testimonial Content */}
                        <div style={{
                            transition: 'all 0.6s ease-in-out',
                            opacity: 1
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                marginBottom: '20px'
                            }}>
                                {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                    <Star
                                        key={i}
                                        size={24}
                                        style={{
                                            color: '#FFD700',
                                            fill: '#FFD700',
                                            marginRight: '5px',
                                            animation: `starTwinkle 2s ease-in-out ${i * 0.2}s infinite alternate`
                                        }}
                                    />
                                ))}
                            </div>

                            <blockquote style={{
                                fontSize: '24px',
                                fontWeight: '500',
                                color: 'white',
                                lineHeight: '1.6',
                                margin: '0 0 30px 0',
                                fontStyle: 'italic',
                                minHeight: '80px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                "{testimonials[activeTestimonial].content}"
                            </blockquote>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '15px',
                                marginBottom: '30px'
                            }}>
                                <img
                                    src={testimonials[activeTestimonial].image}
                                    alt={testimonials[activeTestimonial].name}
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '3px solid rgba(255, 255, 255, 0.3)',
                                        transition: 'all 0.3s ease'
                                    }}
                                />
                                <div style={{ textAlign: 'left' }}>
                                    <div style={{
                                        color: 'white',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        margin: '0 0 5px 0'
                                    }}>
                                        {testimonials[activeTestimonial].name}
                                    </div>
                                    <div style={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        fontSize: '14px'
                                    }}>
                                        {testimonials[activeTestimonial].role}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Progress Indicators */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '10px',
                            marginBottom: '20px'
                        }}>
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveTestimonial(index)}
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: activeTestimonial === index ? 'white' : 'rgba(255, 255, 255, 0.3)',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        transform: activeTestimonial === index ? 'scale(1.2)' : 'scale(1)'
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    <style>{`
                    @keyframes progressBar {
                        0% {
                            transform: scaleX(0);
                        }
                        100% {
                            transform: scaleX(1);
                        }
                    }

                    @keyframes starTwinkle {
                        0% {
                            opacity: 0.7;
                            transform: scale(1);
                        }
                        100% {
                            opacity: 1;
                            transform: scale(1.1);
                        }
                    }

                    @media (max-width: 768px) {
                        .testimonial-content {
                            padding: 30px 20px !important;
                        }
                        
                        .testimonial-quote {
                            font-size: 18px !important;
                        }
                        
                        .navigation-arrow {
                            display: none !important;
                        }
                    }
                `}</style>
                </section>
            </div>


            {/* FAQ Section */}
            <section style={{
                padding: '80px 20px',
                maxWidth: '1000px',
                margin: '0 auto'
            }}>
                <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                    <h2 style={{
                        color: 'white',
                        fontSize: '36px',
                        fontWeight: '700',
                        margin: '0 0 20px 0'
                    }}>
                        Frequently Asked Questions
                    </h2>
                    <p style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '18px',
                        maxWidth: '600px',
                        margin: '0 auto'
                    }}>
                        Find answers to common questions about our expense tracking platform.
                    </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {faqs.map((faq, index) => (
                        <div
                            key={index}
                            style={{
                                background: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '15px',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                overflow: 'hidden',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <button
                                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                style={{
                                    width: '100%',
                                    padding: '25px 30px',
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseOver={(e) => {
                                    if (openFaq !== index) {
                                        e.currentTarget.parentElement.style.background = 'rgba(255, 255, 255, 0.15)';
                                    }
                                }}
                                onMouseOut={(e) => {
                                    if (openFaq !== index) {
                                        e.currentTarget.parentElement.style.background = 'rgba(255, 255, 255, 0.1)';
                                    }
                                }}
                            >
                                <span>{faq.question}</span>
                                {openFaq === index ? (
                                    <ChevronUp size={24} style={{ color: 'rgba(255, 255, 255, 0.8)' }} />
                                ) : (
                                    <ChevronDown size={24} style={{ color: 'rgba(255, 255, 255, 0.8)' }} />
                                )}
                            </button>

                            {openFaq === index && (
                                <div style={{
                                    padding: '0 30px 25px 30px',
                                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                    animation: 'fadeIn 0.3s ease-in-out'
                                }}>
                                    <p style={{
                                        color: 'rgba(255, 255, 255, 0.8)',
                                        fontSize: '16px',
                                        lineHeight: '1.6',
                                        margin: '20px 0 0 0'
                                    }}>
                                        {faq.answer}
                                    </p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA Section */}
            <section style={{
                padding: '80px 20px',
                maxWidth: '1000px',
                margin: '0 auto',
                textAlign: 'center'
            }}>
                <div style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    padding: '60px 40px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <h2 style={{
                        color: 'white',
                        fontSize: '36px',
                        fontWeight: '700',
                        margin: '0 0 20px 0'
                    }}>
                        Ready to Transform Your Finances?
                    </h2>
                    <p style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '18px',
                        margin: '0 0 40px 0',
                        maxWidth: '600px',
                        marginLeft: 'auto',
                        marginRight: 'auto'
                    }}>
                        Join thousands of users who have already taken control of their financial future. Start your free trial today!
                    </p>
                    <button
                        onClick={handleRegister}
                        style={{
                            background: 'white',
                            border: 'none',
                            borderRadius: '15px',
                            color: '#667eea',
                            padding: '18px 40px',
                            fontSize: '18px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '12px',
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-3px)';
                            e.target.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.3)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.2)';
                        }}
                    >
                        <PlusCircle size={20} />
                        Get Started Free
                    </button>
                </div>
            </section>
            <Footer />
        </div>
    )
}

export default Index;